// Mon Profil - Paramètres utilisateur connecté
import { useState, useRef, useEffect } from "react";
import {
  User,
  Mail,
  Lock,
  Globe,
  Bell,
  Save,
  Loader2,
  Eye,
  EyeOff,
  Crown,
  Camera,
  Upload
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

// Rôle de l'utilisateur (devrait venir du backend)
const USER_ROLE = {
  id: "super_admin",
  name: "Super Admin",
  description: "Gestionnaire toutes plateformes, accès illimité",
  color: "text-amber-400",
  bgColor: "bg-amber-500/20"
};

export default function MonProfil() {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Données profil
  const [profil, setProfil] = useState({
    nom: user?.name || "",
    email: user?.email || "",
    telephone: "",
    langue: "fr",
    fuseau_horaire: "Europe/Paris",
    photo: user?.avatar_url || user?.picture || null
  });

  // Charger les données depuis le backend ou utiliser les données locales
  useEffect(() => {
    const loadProfile = async () => {
      // Utiliser d'abord les données de l'utilisateur connecté
      if (user) {
        setProfil(prev => ({
          ...prev,
          nom: user.name || prev.nom,
          email: user.email || prev.email,
          photo: user.avatar_url || user.picture || prev.photo
        }));
      }
      
      if (!user?.email) {
        setLoading(false);
        return;
      }
      
      const API = process.env.REACT_APP_BACKEND_URL;
      try {
        // Essayer de charger des données supplémentaires depuis le backend
        const response = await fetch(`${API}/api/hub/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setProfil(prev => ({
            ...prev,
            nom: data.name || prev.nom,
            email: data.email || prev.email,
            photo: data.avatar_url || data.picture || prev.photo
          }));
        }
      } catch (err) {
        // Ignorer les erreurs - on utilise les données locales
        console.log('Utilisation des données utilisateur locales');
      } finally {
        setLoading(false);
      }
    };
    
    loadProfile();
  }, [user]);

  // Mot de passe
  const [passwords, setPasswords] = useState({
    ancien: "",
    nouveau: "",
    confirmation: ""
  });

  // Préférences notifications
  const [notifications, setNotifications] = useState({
    email_tickets: true,
    email_missions: true,
    push_urgents: true,
    resume_quotidien: false
  });

  const handleSaveProfil = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    toast.success("Profil mis à jour");
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (passwords.nouveau !== passwords.confirmation) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (passwords.nouveau.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    toast.success("Mot de passe modifié");
    setPasswords({ ancien: "", nouveau: "", confirmation: "" });
    setSaving(false);
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', file);
      
      // D'abord créer une preview locale immédiatement pour un meilleur UX
      const localUrl = URL.createObjectURL(file);
      setProfil(prev => ({...prev, photo: localUrl}));
      
      // Construire les headers avec l'email de l'utilisateur pour fallback auth
      const headers = {};
      if (user?.email) {
        headers['X-User-Email'] = user.email;
      }
      
      // Ensuite envoyer au serveur en arrière-plan
      const response = await fetch(`${API}/api/management/users/me/avatar`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.warn('Upload avatar failed:', errorText);
        toast.error("Erreur de sauvegarde - veuillez vous reconnecter");
        return;
      }
      
      // Parser la réponse seulement si le serveur a répondu OK
      const responseData = await response.json();
      
      // Mettre à jour avec l'URL serveur
      if (responseData.avatar_url) {
        setProfil(prev => ({...prev, photo: responseData.avatar_url}));
      }
      
      toast.success("Photo enregistrée ✓");
    } catch (err) {
      console.error('Erreur upload avatar:', err);
      toast.error(err.message || "Erreur lors de l'upload");
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Mon Profil</h1>
          <p className="text-zinc-400 mt-1">Gérez vos informations personnelles</p>
        </div>
      </div>

      {/* Informations personnelles */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-white">Informations personnelles</CardTitle>
              <CardDescription>Votre identité sur OmniHub</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-6 mb-6">
            {/* Avatar avec upload */}
            <div className="relative group">
              {profil.photo ? (
                <img 
                  src={profil.photo} 
                  alt={profil.nom}
                  className="w-24 h-24 rounded-full object-cover ring-4 ring-white/10"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-3xl font-bold text-white ring-4 ring-white/10">
                  {profil.nom?.charAt(0) || '?'}
                </div>
              )}
              {/* Overlay upload */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            
            <div className="flex-1">
              <p className="text-xl font-medium text-white">{profil.nom}</p>
              <p className="text-sm text-zinc-400">{profil.email}</p>
              
              {/* Rôle */}
              <div className="flex items-center gap-2 mt-3">
                <Badge className={`${USER_ROLE.bgColor} ${USER_ROLE.color} px-3 py-1`}>
                  <Crown className="w-3 h-3 mr-1.5" />
                  {USER_ROLE.name}
                </Badge>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{USER_ROLE.description}</p>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-400 mt-2 px-0"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-1" />
                Changer la photo
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Nom complet</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input 
                  value={profil.nom}
                  onChange={(e) => setProfil({...profil, nom: e.target.value})}
                  className="pl-10 bg-zinc-800 border-white/10"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Adresse email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input 
                  type="email"
                  value={profil.email}
                  onChange={(e) => setProfil({...profil, email: e.target.value})}
                  className="pl-10 bg-zinc-800 border-white/10"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Téléphone</Label>
              <Input 
                value={profil.telephone}
                onChange={(e) => setProfil({...profil, telephone: e.target.value})}
                className="mt-1 bg-zinc-800 border-white/10"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={handleSaveProfil} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Langue et préférences */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-white">Langue et zone</CardTitle>
              <CardDescription>Paramètres régionaux</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Langue de l'interface</Label>
              <Select value={profil.langue} onValueChange={(v) => setProfil({...profil, langue: v})}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400">Fuseau horaire</Label>
              <Select value={profil.fuseau_horaire} onValueChange={(v) => setProfil({...profil, fuseau_horaire: v})}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Europe/Paris">Europe/Paris (UTC+1)</SelectItem>
                  <SelectItem value="Europe/Brussels">Europe/Brussels (UTC+1)</SelectItem>
                  <SelectItem value="America/Montreal">America/Montreal (UTC-5)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sécurité - Mot de passe */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Lock className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <CardTitle className="text-white">Sécurité</CardTitle>
              <CardDescription>Modifier votre mot de passe</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-zinc-400">Mot de passe actuel</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input 
                type={showPassword ? "text" : "password"}
                value={passwords.ancien}
                onChange={(e) => setPasswords({...passwords, ancien: e.target.value})}
                className="pl-10 pr-10 bg-zinc-800 border-white/10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Nouveau mot de passe</Label>
              <Input 
                type="password"
                value={passwords.nouveau}
                onChange={(e) => setPasswords({...passwords, nouveau: e.target.value})}
                className="mt-1 bg-zinc-800 border-white/10"
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Confirmer le mot de passe</Label>
              <Input 
                type="password"
                value={passwords.confirmation}
                onChange={(e) => setPasswords({...passwords, confirmation: e.target.value})}
                className="mt-1 bg-zinc-800 border-white/10"
                placeholder="••••••••"
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button 
              onClick={handleChangePassword} 
              disabled={saving || !passwords.ancien || !passwords.nouveau}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              Modifier le mot de passe
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-white">Notifications</CardTitle>
              <CardDescription>Gérez vos préférences de notification</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-white/5">
            <div>
              <p className="text-white font-medium">Tickets CxO par email</p>
              <p className="text-sm text-zinc-500">Recevoir un email pour les nouveaux tickets</p>
            </div>
            <Switch 
              checked={notifications.email_tickets}
              onCheckedChange={(v) => setNotifications({...notifications, email_tickets: v})}
            />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-white/5">
            <div>
              <p className="text-white font-medium">Mises à jour des missions</p>
              <p className="text-sm text-zinc-500">Notifications sur les changements de statut</p>
            </div>
            <Switch 
              checked={notifications.email_missions}
              onCheckedChange={(v) => setNotifications({...notifications, email_missions: v})}
            />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-white/5">
            <div>
              <p className="text-white font-medium">Alertes urgentes (push)</p>
              <p className="text-sm text-zinc-500">Notifications push pour les tickets critiques</p>
            </div>
            <Switch 
              checked={notifications.push_urgents}
              onCheckedChange={(v) => setNotifications({...notifications, push_urgents: v})}
            />
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-white font-medium">Résumé quotidien</p>
              <p className="text-sm text-zinc-500">Email récapitulatif chaque matin</p>
            </div>
            <Switch 
              checked={notifications.resume_quotidien}
              onCheckedChange={(v) => setNotifications({...notifications, resume_quotidien: v})}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
